import { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import styled, { createGlobalStyle } from 'styled-components';
import montserratBold from '@fontsource/montserrat/files/montserrat-latin-700-normal.woff2';

import { cleanContact, formatPhone } from '../../sundays';
import { CURRENT_SUNDAY_TERMS, SUNDAY_TERMS } from '../../sundayTerms';
import { CloseIcon, colors } from '../../styles';

// Billing B1.5. mode 'agree': the host must scroll to the end of the terms, tick
// the box and give the on-site contact before Sundays open. mode 'view': the
// terms version the host agreed to, read only. mode 'contact': change the contact.

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.mediumBorder};
  margin-bottom: 20px;

  button {
    border: none;
    background-color: transparent;
    padding: 0;

    svg {
      height: 14px;
      width: 14px;
    }
  }
`;

// The site loads Montserrat in weight 400 only (src/index.js), and phones show no
// bold for it. This bold face is for the section names in this box only, so no
// other text on the site changes.
const TermsBoldFont = createGlobalStyle`
  @font-face {
    font-family: 'Yedding Terms Bold';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url(${montserratBold}) format('woff2');
  }
`;

const Terms = styled.div`
  font-size: 14px;
  line-height: 1.5;
  max-height: 40vh; /* leaves room for the tick box, fields and buttons */
  overflow-y: auto;
  padding: 16px;
  border-radius: 8px;
  background-color: ${colors.secondaryBackground};
  margin-bottom: 20px;

  p {
    margin-bottom: 10px;
  }

  strong {
    font-family: 'Yedding Terms Bold', Montserrat, Arial, sans-serif;
    font-weight: 700; /* section names, for easy reading */
  }

  h3 {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  ol {
    padding-left: 22px;
    margin-bottom: 12px;
  }

  li {
    margin-bottom: 6px;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

const ScrollHint = styled.div`
  font-size: 13px;
  color: ${colors.defaultText};
  opacity: 0.7;
  margin: -12px 0 16px;
`;

const Fields = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 24px;
`;

const FieldLabel = styled.label`
  display: grid;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;

  input {
    font-weight: 400;
    border: 1px solid ${colors.mediumBorder};
    border-radius: 4px;
    padding: 10px 12px;
    font-size: 16px; /* 16px stops iPhone Safari zooming in */
  }

  input:disabled {
    background-color: ${colors.secondaryBackground};
  }
`;

const FieldNote = styled.span`
  font-size: 13px;
  font-weight: 400;
`;

const ErrorText = styled.div`
  color: #c0392b;
  font-size: 13px;
  margin-bottom: 12px;
`;

const Agree = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 24px;

  input {
    margin-top: 3px;
    width: 16px;
    height: 16px;
    accent-color: ${colors.primaryButton};
    flex-shrink: 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${colors.mediumBorder};
  padding-top: 20px;
`;

const CancelButton = styled.button`
  font-weight: 500;
  color: ${colors.defaultText};
  background-color: transparent;
  font-size: 14px;
  padding: 0 0 2px;
  border: none;
  border-bottom: 1px solid ${colors.defaultText};
  line-height: 1;
`;

const SubmitButton = styled.button`
  font-weight: 500;
  color: white;
  background-color: ${colors.primaryButton};
  font-size: 14px;
  border-radius: 4px;
  padding: 12px 32px;
  border: none;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const TermsText = ({ terms }) => (
  <>
    <h3>{terms.title}</h3>
    <p>{terms.intro}</p>
    {terms.sections.map((section) => (
      <div key={section.heading}>
        <p>
          <strong>{section.heading}</strong> {section.text}
        </p>
        {section.list && (
          <ol>
            {section.list.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        )}
      </div>
    ))}
  </>
);

// Within 8px of the end counts as the end (zoom and rounding on phones).
const atEnd = (box) => box.scrollHeight - box.scrollTop - box.clientHeight <= 8;

const SundayTermsModal = ({ mode = 'agree', version, initialContact, onSubmit, onCancel, saving = false, error = false }) => {
  const terms = (version && SUNDAY_TERMS[version]) || CURRENT_SUNDAY_TERMS;
  const showTerms = mode !== 'contact';
  const showForm = mode !== 'view';
  const boxRef = useRef(null);
  const [readToEnd, setReadToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState(initialContact?.name || '');
  const [phone, setPhone] = useState(initialContact?.phone ? formatPhone(initialContact.phone) : '');
  const [phoneTouched, setPhoneTouched] = useState(false);

  // Short text that needs no scroll (a tall screen) counts as read.
  useEffect(() => {
    if (mode === 'agree' && boxRef.current && atEnd(boxRef.current)) {
      setReadToEnd(true);
    }
  }, [mode]);

  const contact = cleanContact({ name, phone });
  const phoneBad = phoneTouched && phone.trim() !== '' && !cleanContact({ name: 'xx', phone });
  const canSubmit = !saving && Boolean(contact) && (mode !== 'agree' || (readToEnd && agreed));
  const title = mode === 'contact' ? 'Sunday on-site contact' : 'Sunday terms';

  return (
    <Modal centered show onHide={onCancel}>
      <TermsBoldFont />
      <Modal.Body>
        <Title>
          <div>{title}</div>
          <button onClick={onCancel} aria-label="Close">
            <CloseIcon />
          </button>
        </Title>
        {showTerms && (
          <Terms
            ref={boxRef}
            data-testid="sunday-terms"
            tabIndex={0}
            onScroll={(e) => !readToEnd && atEnd(e.currentTarget) && setReadToEnd(true)}
          >
            <TermsText terms={terms} />
          </Terms>
        )}
        {mode === 'agree' && !readToEnd && <ScrollHint>Scroll to the end to continue.</ScrollHint>}
        {mode === 'agree' && (
          <Agree style={readToEnd ? undefined : { cursor: 'not-allowed', opacity: 0.5 }}>
            <input type="checkbox" checked={agreed} disabled={!readToEnd} onChange={(e) => setAgreed(e.target.checked)} />
            <span>{terms.agree}</span>
          </Agree>
        )}
        {showForm && (
          <Fields>
            <FieldLabel>
              On-site contact full name
              <input type="text" autoComplete="name" value={name} disabled={saving} onChange={(e) => setName(e.target.value)} />
            </FieldLabel>
            <FieldLabel>
              On-site contact phone number
              <input
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                value={phone}
                disabled={saving}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => setPhoneTouched(true)}
              />
            </FieldLabel>
            {phoneBad && <FieldNote style={{ color: '#c0392b', marginTop: -8 }}>Enter a 10-digit US phone number.</FieldNote>}
            <FieldNote>We show this name and phone to the booker in their Reservations on the Monday before each Sunday event.</FieldNote>
          </Fields>
        )}
        {error && <ErrorText>We could not save this. Please try again.</ErrorText>}
        <Buttons>
          <CancelButton onClick={onCancel}>{showForm ? 'Cancel' : 'Close'}</CancelButton>
          {showForm && (
            <SubmitButton disabled={!canSubmit} onClick={() => canSubmit && onSubmit(contact)}>
              {saving ? 'Saving...' : mode === 'contact' ? 'Save contact' : 'Open Sundays'}
            </SubmitButton>
          )}
        </Buttons>
      </Modal.Body>
    </Modal>
  );
};

export default SundayTermsModal;
